import React, { FormEvent, useEffect, useRef, useState } from 'react';
import {
  useCategoryLeaderboardGroupState,
  useFeatureLeaderboardGroupState,
} from '@utils/hook/userPreferences/UseGroupState';
import { useCategoryOrSegmentsLeaderboardGroupEventState } from '../../utils/CategoryOrSegmentsLeaderboardGroupContext';
import {
  EXPLORE_LEADERBOARD_TABLE,
  GroupContext,
  MAX_GROUP_NAME_ERROR_MESSAGE,
  MAX_GROUP_NAME_LENGTH,
  PreferenceResponseStatus,
  UNKNOWN_ERROR_MESSAGE,
} from '@utils/userPreferencesUtils/Types';
import {
  CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
  groupElementClickedTrackingFunction,
  TableElementClickedParam,
} from '@utils/page-analytics/model/GroupElementClickTracking';
import { FilterTitle, PageName, PageType } from '@utils/pageConfig/Types';
import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import CreateGroupModal from '@components/create-group-modal/CreateGroupModal';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getCountryIdsByCountryNames } from '@utils/CountryUtils';
import { IRootState } from '@state/reducers/Reducers';

export interface Props {
  context: GroupContext;
  sortOrder: number;
}

/**
 * @return Modal
 *
 * @remarks
 * a pop-up modal to let user enter a unique group name with Create Group and cancel buttons
 *
 * @param context   - contains retailer client and country
 * @param sortOrder - number to represent the order for groups in ascending order based on create sequence
 * **/
export const CreateCategoryOrSegmentsLeaderboardModalGroup: React.FC<Props> = ({ context, sortOrder }) => {
  const pagesConfig = usePageConfig();
  const [groupName, setGroupName] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const retailerClientState = useSelector((state: IRootState) => state.retailerClient);
  const { createCategoryLeaderboardGroup } = useCategoryLeaderboardGroupState(context);
  const { createFeatureLeaderboardGroup } = useFeatureLeaderboardGroupState(context);
  const {
    selectedProductGroupOrSegments,
    clearSelectedPgsOrSegments,
    isCreateModalGroupPopUp,
    setIsCreateModalGroupPopUp,
    currentSelectedFeature,
  } = useCategoryOrSegmentsLeaderboardGroupEventState();
  const { track } = useAnalytics();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);

  const onCancel = () => {
    setIsSaving(false);
    setError('');
    setGroupName('');
    setIsCreateModalGroupPopUp(false);
  };

  const constructCategoryLeaderboardGroupPreference = (trimGroupName: string) => {
    createCategoryLeaderboardGroup.mutate(
      {
        key: trimGroupName,
        data: {
          productGroup: selectedProductGroupOrSegments,
          showPerformanceWithin: currentSelectedFeature.feature,
          sortOrder: sortOrder,
          clientId: retailerClientState.clientId,
        },
      },
      {
        onSuccess: (response) => {
          setIsSaving(false);
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            setGroupName('');
            clearSelectedPgsOrSegments();
            onCancel();
          } else {
            setError(response.message);
          }
        },
        onError: () => {
          setError(UNKNOWN_ERROR_MESSAGE);
          setIsSaving(false);
        },
      },
    );
  };

  const constructFeatureLeaderboardGroupPreference = (trimGroupName: string) => {
    createFeatureLeaderboardGroup.mutate(
      {
        key: trimGroupName,
        data: {
          value:
            currentSelectedFeature.feature === FilterTitle.COUNTRY
              ? getCountryIdsByCountryNames(selectedProductGroupOrSegments, pagesConfig.clientCountries)
              : selectedProductGroupOrSegments,
          showPerformanceWithin: currentSelectedFeature.feature,
          sortOrder: sortOrder,
          clientId: retailerClientState.clientId,
        },
      },
      {
        onSuccess: (response) => {
          setIsSaving(false);
          if (response.status === PreferenceResponseStatus.SUCCESS) {
            setGroupName('');
            clearSelectedPgsOrSegments();
            onCancel();
          } else {
            setError(response.message);
          }
        },
        onError: () => {
          setError(UNKNOWN_ERROR_MESSAGE);
          setIsSaving(false);
        },
      },
    );
  };

  const createGroup = (trimGroupName: string) => {
    const isCategoryFeature = currentSelectedFeature === null || currentSelectedFeature.feature === 'Category';

    // Call mutate to create group
    isCategoryFeature
      ? constructCategoryLeaderboardGroupPreference(trimGroupName)
      : constructFeatureLeaderboardGroupPreference(trimGroupName);
  };

  //Function to create group
  const onSaveGroup = (e: FormEvent) => {
    e.preventDefault();

    //Amplitude Tracking when Create Group button is click
    const trackingParam: TableElementClickedParam = {
      eventTitle: CATEGORY_GROUP_TABLE_ELEMENT_CLICKED_TITLE,
      elementName: `Create ${currentSelectedFeature.feature} Group`,
      elementType: `Table ${currentSelectedFeature.feature} Group`,
      track: track,
      optionChosen: selectedProductGroupOrSegments.flatMap((selectedPg) => selectedPg).join(', '),
      pageName: PageName.EXPLORE,
      pageType: PageType.EXPLORE,
      sideFilters: sideFiltersState.appliedFilters,
      access: pagesConfig.access,
      tableName: EXPLORE_LEADERBOARD_TABLE,
    };

    groupElementClickedTrackingFunction(trackingParam);

    const nameTrimmed = (groupName ?? '').trim();

    if (!nameTrimmed || nameTrimmed.length >= MAX_GROUP_NAME_LENGTH) {
      setError(MAX_GROUP_NAME_ERROR_MESSAGE);
      return;
    }

    setIsSaving(true);
    setError('');

    createGroup(nameTrimmed);
  };

  useEffect(() => {
    if (isCreateModalGroupPopUp && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreateModalGroupPopUp]);

  return (
    <div>
      <CreateGroupModal
        isCreateModalGroupPopUp={isCreateModalGroupPopUp}
        createMessage="Create Group"
        onCancel={onCancel}
        isSaving={isSaving}
        onSaveGroup={onSaveGroup}
        title={'Create Explore Group'}
        setGroupName={setGroupName}
        error={error}
      />
    </div>
  );
};
